import { useEffect, useRef, useState } from "react";
import type { ClipboardEvent, DragEvent } from "react";

import type { ComposerFileAttachment, ComposerImageAttachment } from "../../composerDraftStore";
import {
  admitComposerAttachmentBatch,
  publishAdmittedComposerImages,
  reportComposerAttachmentIntakeResult,
  revokeDiscardedComposerImagePreviews,
  stageComposerFileBatch,
  shouldSkipComposerAttachmentIntake,
} from "./composerAttachmentIntake";
import { getClipboardFiles } from "./getClipboardFiles";

interface UseComposerAttachmentIntakeInput {
  readonly images: ReadonlyArray<ComposerImageAttachment>;
  readonly files: ReadonlyArray<ComposerFileAttachment>;
  readonly scopeKey: string;
  readonly isDisabled: () => boolean;
  readonly blockedReason?: string | null;
  readonly onBlocked?: ((message: string) => void) | undefined;
  readonly onAddImages: (images: ComposerImageAttachment[]) => void;
  readonly onAddFiles: (files: ComposerFileAttachment[]) => void;
  readonly onError: (message: string | null) => void;
  readonly onAfterDrop?: (() => void) | undefined;
  /** User-input responses carry every selected attachment as a file. */
  readonly fileOnly?: boolean | undefined;
}

export interface ComposerAttachmentIntakeController {
  readonly isDragOver: boolean;
  readonly addFiles: (files: ReadonlyArray<File>) => void;
  readonly onPaste: (event: ClipboardEvent<HTMLElement>) => void;
  readonly onDragEnter: (event: DragEvent<HTMLDivElement>) => void;
  readonly onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  readonly onDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  readonly onDrop: (event: DragEvent<HTMLDivElement>) => void;
}

function pendingFilesForScope(reservations: Map<string, number>, scopeKey: string): number {
  return reservations.get(scopeKey) ?? 0;
}

function updatePendingFileReservation(input: {
  readonly reservations: Map<string, number>;
  readonly scopeKey: string;
  readonly count: number;
}): void {
  const next = pendingFilesForScope(input.reservations, input.scopeKey) + input.count;
  if (next === 0) input.reservations.delete(input.scopeKey);
  else input.reservations.set(input.scopeKey, next);
}

/**
 * Shared local intake for every Forge message composer. File bytes are stored
 * before their refs join the draft, so the picker never starts a network action.
 *
 * The caller owns the attachment list. This hook owns only drag interaction
 * state and reports accepted attachments through controlled callbacks.
 */
export function useComposerAttachmentIntake(
  input: UseComposerAttachmentIntakeInput,
): ComposerAttachmentIntakeController {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);
  const currentImagesRef = useRef(input.images);
  currentImagesRef.current = input.images;
  const currentFilesRef = useRef(input.files);
  currentFilesRef.current = input.files;
  const scopeKeyRef = useRef(input.scopeKey);
  scopeKeyRef.current = input.scopeKey;
  const pendingFileReservationsRef = useRef(new Map<string, number>());
  const mountedRef = useRef(true);

  useEffect(() => {
    dragDepthRef.current = 0;
    setIsDragOver(false);
  }, [input.scopeKey]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const addFiles = async (files: ReadonlyArray<File>) => {
    if (
      shouldSkipComposerAttachmentIntake({
        disabled: input.isDisabled(),
        fileCount: files.length,
        blockedReason: input.blockedReason,
        onBlocked: input.onBlocked,
      })
    )
      return;

    const admitted = admitComposerAttachmentBatch({
      files,
      currentAttachmentCount:
        currentImagesRef.current.length +
        currentFilesRef.current.length +
        pendingFilesForScope(pendingFileReservationsRef.current, input.scopeKey),
      fileOnly: input.fileOnly ?? false,
    });
    const { images: nextImages, files: acceptedFiles } = admitted;
    let error = admitted.error;

    currentImagesRef.current = publishAdmittedComposerImages({
      currentImages: currentImagesRef.current,
      images: nextImages,
      publish: input.onAddImages,
    });
    if (acceptedFiles.length === 0) {
      input.onError(error);
      return;
    }

    const batchScopeKey = input.scopeKey;
    updatePendingFileReservation({
      reservations: pendingFileReservationsRef.current,
      scopeKey: batchScopeKey,
      count: acceptedFiles.length,
    });
    const staged = await stageComposerFileBatch({
      files: acceptedFiles,
      scopeIsActive: () => mountedRef.current && scopeKeyRef.current === batchScopeKey,
      currentFiles: () => currentFilesRef.current,
      publish: (nextFiles) => {
        currentFilesRef.current = nextFiles;
        input.onAddFiles(nextFiles.slice(-acceptedFiles.length));
      },
    });
    updatePendingFileReservation({
      reservations: pendingFileReservationsRef.current,
      scopeKey: batchScopeKey,
      count: -acceptedFiles.length,
    });
    revokeDiscardedComposerImagePreviews({ discarded: staged.discarded, images: nextImages });
    reportComposerAttachmentIntakeResult({
      discarded: staged.discarded,
      error: staged.error ?? error,
      onError: input.onError,
    });
  };

  const onPaste = (event: ClipboardEvent<HTMLElement>) => {
    const files = getClipboardFiles(event.clipboardData);
    if (files.length === 0) return;
    event.preventDefault();
    void addFiles(files);
  };

  const onDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (input.isDisabled() || !event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOver(true);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (input.isDisabled() || !event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  };

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragOver(false);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (input.isDisabled() || !event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    void addFiles(Array.from(event.dataTransfer.files));
    input.onAfterDrop?.();
  };

  return {
    isDragOver,
    addFiles,
    onPaste,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
  };
}

/** @deprecated Use `useComposerAttachmentIntake`. */
export const useComposerImageAttachments = useComposerAttachmentIntake;
/** @deprecated Use `ComposerAttachmentIntakeController`. */
export type ComposerImageAttachmentController = ComposerAttachmentIntakeController;
